import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { TMapState } from "../types/map";
import { APP_CONFIG, GOOGLE_MAP_MODE_NAMES } from "../constants/constants";
import { useLazyGImageryMetaQuery } from "../api/network/mapApiServices";
import { useTranslation } from "react-i18next";

export const useGMapMode = () => {
  const dispatch = useDispatch();
  const { t: tMain } = useTranslation();

  const mapState = useSelector(
    (state: { commonReducer: { mapState: TMapState } }) =>
      state.commonReducer.mapState
  );

  const [gImageryMetaApiTrigger] = useLazyGImageryMetaQuery();

  const gMapIsInMode = (
    map: google.maps.Map,
    mode: GOOGLE_MAP_MODE_NAMES // "roadmap" | "satellite" | "birdseye" | "streetview"
  ) => {
    switch (mode) {
      case GOOGLE_MAP_MODE_NAMES.BIRDSEYE:
        return (
          map?.getTilt() === 45 &&
          map?.getMapTypeId() === google.maps.MapTypeId.HYBRID
        );
      case GOOGLE_MAP_MODE_NAMES.STREETVIEW:
        return map?.getStreetView()?.getVisible();
      case GOOGLE_MAP_MODE_NAMES.SATELLITE:
        return (
          map?.getMapTypeId() === google.maps.MapTypeId.SATELLITE ||
          map?.getMapTypeId() === google.maps.MapTypeId.HYBRID
        );
      case GOOGLE_MAP_MODE_NAMES.ROADMAP:
      default:
        return (
          map?.getMapTypeId() === google.maps.MapTypeId.ROADMAP ||
          map?.getMapTypeId() === google.maps.MapTypeId.TERRAIN
        );
    }
  };

  const gMapGetMode = (map: google.maps.Map) => {
    if (
      (map?.getTilt() as number) > 0 // === 45
      // && map?.getMapTypeId() === google.maps.MapTypeId.HYBRID
    ) {
      map.setOptions({ rotateControl: true });
      return GOOGLE_MAP_MODE_NAMES.BIRDSEYE;
    } else if (map?.getStreetView()?.getVisible()) {
      return GOOGLE_MAP_MODE_NAMES.STREETVIEW;
    } else if (
      map?.getMapTypeId() === google.maps.MapTypeId.SATELLITE ||
      map?.getMapTypeId() === google.maps.MapTypeId.HYBRID
    ) {
      return GOOGLE_MAP_MODE_NAMES.SATELLITE;
    } else {
      return GOOGLE_MAP_MODE_NAMES.ROADMAP;
    }
  };

  const gMapSetMode = (
    map: google.maps.Map,
    mode: GOOGLE_MAP_MODE_NAMES, // "roadmap" | "satellite" | "birdseye" | "streetview",
    latLng?: google.maps.LatLng,
    additionalOptions?: any
  ) => {
    const lastZoomLevel = map.getZoom();
    const lastMapMode = gMapGetMode(map);
    if (latLng && !!latLng?.lat()) {
      map.setOptions({
        center: latLng,
      });
    }
    if (APP_CONFIG.DEBUG.MAPS)
      console.info("[gMapSetMode] OLD:", lastMapMode, "NEW:", mode);
    // ensures that settings from the old view are cleared before applying the new one.
    if (mode !== GOOGLE_MAP_MODE_NAMES.STREETVIEW) {
      map.getStreetView().setVisible(false);
    }
    if (mode !== GOOGLE_MAP_MODE_NAMES.BIRDSEYE) {
      map.setTilt(0);
      map.setOptions({ rotateControl: false });
    }
    if (
      lastMapMode === GOOGLE_MAP_MODE_NAMES.BIRDSEYE &&
      !!lastZoomLevel &&
      lastZoomLevel >= 18 &&
      mode !== lastMapMode
    ) {
      // if moving away from birdseye, zoom out first
      // to prevent the user from having an impression that nothing happened
      map.setZoom(lastZoomLevel - 1); // or should it be 13 where birseye is not available?
      map.setOptions({ rotateControl: false });
    }
    switch (mode) {
      case GOOGLE_MAP_MODE_NAMES.BIRDSEYE:
        map.setMapTypeId(google.maps.MapTypeId.HYBRID);
        if ((map.getZoom() as number) < 18) {
          map.setZoom(18);
        }
        map.setTilt(45);
        map.setOptions({ rotateControl: true });
        // after 500ms, check if tilt was set
        // setTimeout(() => {
        //   if (map.getTilt() !== 45) {
        //     console.log("tilt was not set, reverting");
        //     gMapSetMode(map, lastMapMode, latLng, additionalOptions);
        //   }
        // }, 500);
        if (APP_CONFIG.DEBUG.MAPS) console.log("TILT CHECK", map.getTilt());
        break;
      case GOOGLE_MAP_MODE_NAMES.STREETVIEW:
        if (latLng) {
          // record last map mode (if not streetview)
          if (lastMapMode !== GOOGLE_MAP_MODE_NAMES.STREETVIEW) {
            (map as any).lastMapModeBeforeStreetview = lastMapMode;
          }
          // close last panorama
          const lastPanorama = map.getStreetView();
          lastPanorama.setVisible(false);
          // instead if retrieving streetview object from the map (above), we create a fresh instance (below)
          // which fixes the issue where the streetview was not launching the second time
          const panorama = new google.maps.StreetViewPanorama(map.getDiv(), {
            pano:
              additionalOptions?.gImageryMetaApiResponse?.pano_id ?? undefined,
            pov: {
              heading: 0,
              pitch: 0,
            },
            zoom: 1,
            enableCloseButton: true,
          });
          // set source to outdoor - prevents streetview from launching nearby 360-degree virtual tour (indoor imagery for businesses, museums, etc)
          panorama.setOptions({
            source: google.maps.StreetViewSource.OUTDOOR,
          } as google.maps.StreetViewPanoramaOptions & {
            source: google.maps.StreetViewSource;
          });
          if (
            !!additionalOptions?.gImageryMetaApiResponse?.location?.lat &&
            !!additionalOptions?.gImageryMetaApiResponse?.location?.lng
          ) {
            const { lat, lng } =
              additionalOptions.gImageryMetaApiResponse.location;
            const panoLocation = new google.maps.LatLng(lat, lng);
            panorama.setPosition(panoLocation);
          } else {
            panorama.setPosition(latLng as google.maps.LatLng);
          }
          // Set initial POV as well
          panorama.setPov({
            heading: 0,
            pitch: 0,
          });
          // safety check to prevent the same listener from being added multiple times
          google.maps.event.clearListeners(panorama, "visible_changed");

          panorama.addListener("visible_changed", () => {
            const panoVisible = panorama.getVisible();
            console.log("visible_changed", panoVisible);
            setTimeout(() => {
              additionalOptions?.onStreetViewVisibleChanged?.(
                panorama.getVisible()
              );
            }, 500);
          });

          // Only register event listener if not skipping (prevents recursive listener registration)
          // if (!additionalOptions?.skipEventListenerRegistration || true) {
          //   const visibleChangedListener = panorama.addListener(
          //     "visible_changed",
          //     () => {
          //       const panoVisible = panorama.getVisible();
          //       console.log("[visible_changed]", panoVisible);
          //       // Only trigger callback once, then remove the listener to prevent recursion
          //       if (additionalOptions?.onStreetViewVisibleChanged) {
          //         additionalOptions.onStreetViewVisibleChanged(panoVisible);
          //         google.maps.event.removeListener(visibleChangedListener);
          //       }
          //       // if (panorama.getVisible()) {
          //       //   map.setZoom(1);
          //       //   map.setTilt(0);
          //       //   map.setOptions({ rotateControl: false });
          //       // }
          //     }
          //   );
          // }
          // setting the new streetview to the map and marking as visible
          map.setStreetView(panorama);
          panorama.setVisible(true);
          setTimeout(() => {
            google.maps.event.trigger(panorama, "resize");
          }, 200);
          // check here
          // fetch(GOOGLEMAPS_ENDPOINTS.STREETVIEW_META(latLng.lat(), latLng.lng()))
          //   .then((response) => response.json())
          //   .then((data) => {
          //     console.log("need to check data", data);
          //     // const panorama = map.getStreetView();
          //     // panorama.setPosition(latLng as google.maps.LatLng);
          //     // panorama.setPov({
          //     //   heading: data.heading,
          //     //   pitch: data.pitch,
          //     // });
          //     // map.setStreetView(panorama);
          //   });
        } else {
          toast.error("This location does not have street view available.");
        }
        break;
      case GOOGLE_MAP_MODE_NAMES.SATELLITE:
        map.setOptions({
          // SetTilt(0) and rotateControl are now handled by the master reset block above.
          // rotateControl: false,
          ...((map.getZoom() as number) > 15 ? { zoom: 15 } : {}),
        });
        // map.setTilt(0);
        map.setMapTypeId(google.maps.MapTypeId.SATELLITE);
        break;
      case GOOGLE_MAP_MODE_NAMES.ROADMAP:
      default:
        map.setMapTypeId(google.maps.MapTypeId.ROADMAP);
    }
  };

  const gMapGetZoom = (map: google.maps.Map) => {
    return map?.getZoom() as number;
  };

  const gMapSetZoom = (map: google.maps.Map, zoom: number) => {
    return map?.setZoom(zoom);
  };

  const gMapEnsureAndLaunchStreetView = ({
    center,
    successCallback,
  }: {
    center: google.maps.LatLng;
    successCallback: (gImageryMetaApiResponse?: any) => void;
  }) => {
    const method = "api" as "js" | "api";
    console.log("[gMapEnsureAndLaunchStreetView] method:", method);
    if (method === "js") {
      var streetViewService = new google.maps.StreetViewService();
      var latlng = new google.maps.LatLng(center.lat(), center.lng());
      var radius = 50; // Search radius in meters

      (streetViewService as any).getPanoramaByLocation(
        latlng,
        radius,
        function (result: any, status: any) {
          if (status == google.maps.StreetViewStatus.OK) {
            // Street View imagery is available at or near this location
            console.log(
              "Street View available. Pano ID: " + result.pano.description
            );
            // You can now set the panorama's position and make it visible
            // map.getStreetView().setPosition(result.location.latLng);
            // map.getStreetView().setVisible(true);
            successCallback(result);
          } else {
            // Street View imagery is not available
            console.log(
              "No Street View results found (Status: " + status + ")"
            );
            // Handle this case (e.g., display an alert or keep map view visible)
          }
        }
      );
    } else {
      gImageryMetaApiTrigger({ lat: center.lat(), lng: center.lng() })
        .unwrap()
        .then((response) => {
          if (APP_CONFIG.DEBUG.MAPS)
            console.log(
              "[gImageryMetaApiTrigger] request",
              { lat: center.lat(), lng: center.lng() },
              "response",
              response
            );
          if ((response?.status as any) === "OK") {
            successCallback(response);
          } else {
            toast.error(
              (APP_CONFIG.DEBUG.ALL ? "[B] " : "") +
                tMain("maps.type_not_supported")
            );
          }
        })
        .catch((error) => {
          console.log("[gImageryMetaApiTrigger]", error);
          toast.error(
            (APP_CONFIG.DEBUG.ALL ? "[C] " : "") +
              tMain("maps.type_not_supported")
          );
        });
    }
  };

  return {
    gMapIsInMode,
    gMapGetMode,
    gMapSetMode,
    gMapGetZoom,
    gMapSetZoom,
    gMapEnsureAndLaunchStreetView,
  };
};
